// public/v3/src/lib/batch-careplan.js — client batch orchestration for the
// care-plan + billing-note batch lane. Collects the per-patient batch records
// (care plan always; billing as CCM combined / BHI / APCM-local), then
// materializes the returned model results into the exact notes the realtime
// path would have saved.
//
// The heavy lifting (prompt build + note assembly) lives in the Task 6/7 gen
// libs; this module only sequences them. The pure record-key helpers live in
// the CJS sibling so they can be Jest-tested (this module imports
// network-touching ESM, so it is exercised at integration/build level).
//
// Care-plan-before-billing ordering: materializeBatchResults applies every
// care_plan row before any billing row (via orderResultsCarePlanFirst) so the
// same-run care plan is SAVED to the store before a billing note materializes.
// Ordering alone does NOT set the "care plan on file" boolean — the BHI
// materializer (materializeBhiBillingResult) RECOMPUTES carePlanOnFile from the
// live store at materialize time (the batch builder froze it false at collect
// time, before any plan was saved). With the care plan already saved by the
// ordering, that recompute yields the realtime-equivalent value — exactly as
// the realtime path sequences (save care plan, then build the bill reading the
// now-current store).

import { buildCarePlanRecord, materializeCarePlanResult } from './careplan-generate';
import {
  buildBillingRecord,
  buildBhiBillingRecord,
  materializeBillingResult,
  materializeBhiBillingResult,
  generateAndSaveBillingNote,
} from './billing-generate';
import { recordKeyFor, parseRecordKey, orderResultsCarePlanFirst } from './batch-careplan-keys.cjs';

export { recordKeyFor };

// Build the batch records for a list of tasks. Each task: { patientId, patient,
// program }. Returns { records, localTasks }:
//   - records:    LLM-bearing batch records ({ recordKey, type, model,
//                 promptBody, assemblerCtx }) submitted to the batch endpoint.
//   - localTasks: APCM/no-LLM billing tasks ({ task, type }) that the realtime
//                 generator assembles instantly (no /ai round trip).
// The care-plan record is always LLM-bearing. Billing routes by program: BHI →
// buildBhiBillingRecord; otherwise buildBillingRecord (which flags APCM months
// with skipBatch so they run locally).
export async function collectBatchRecords(tasks, monthStr) {
  const records = [];
  const localTasks = [];
  for (const t of tasks) {
    const patientId = t.patientId;

    // Care plan — always an LLM record.
    const cp = await buildCarePlanRecord(t.patient, monthStr, {});
    records.push({
      recordKey: recordKeyFor(patientId, monthStr, 'care_plan'),
      type: 'care_plan',
      model: cp.model,
      promptBody: cp.promptBody,
      assemblerCtx: cp.assemblerCtx,
    });

    // Billing.
    if (t.program === 'bhi') {
      const bhi = await buildBhiBillingRecord(t.patient, monthStr, {});
      if (bhi) {
        records.push({
          recordKey: recordKeyFor(patientId, monthStr, 'bhi_bn'),
          type: 'bhi_bn',
          model: bhi.model,
          promptBody: bhi.promptBody,
          assemblerCtx: bhi.assemblerCtx,
        });
      }
    } else {
      const bn = await buildBillingRecord(t.patient, monthStr, {});
      if (bn && bn.skipBatch) {
        // APCM / deterministic: assembled locally with aiText='' (no LLM).
        localTasks.push({ task: t, type: 'bn_combined' });
      } else if (bn) {
        records.push({
          recordKey: recordKeyFor(patientId, monthStr, 'bn_combined'),
          type: 'bn_combined',
          model: bn.model,
          promptBody: bn.promptBody,
          assemblerCtx: bn.assemblerCtx,
        });
      }
    }
  }
  return { records, localTasks };
}

// Materialize the batch result rows into saved notes. resultRows carry
// { record_key, status, result_text, assembler_ctx, error? }. Care_plan rows
// are applied first (see ordering note above). For each row: parse the key,
// skip + count non-'applied' statuses, else dispatch by type to the matching
// materializer using the LIVE roster patient (patientsById[patientId]) for the
// save target and the carried assembler_ctx for the note text. Per-row
// try/catch so one failure doesn't abort the batch.
//
// Signature note: materializeCarePlanResult takes (patient, monthStr, rawText)
// — care plan derives its phiMap from the patient, no ctx. The billing
// materializers take (patient, monthStr, rawText, ctx): the save patientId
// comes from the live patient, the note text from ctx (both the same person).
export async function materializeBatchResults(monthStr, resultRows, patientsById) {
  let applied = 0;
  let failed = 0;
  const errors = [];
  for (const row of orderResultsCarePlanFirst(resultRows)) {
    const { patientId, type } = parseRecordKey(row.record_key);
    if (row.status !== 'applied') {
      failed++;
      if (row.error) errors.push(row.error);
      continue;
    }
    const patient = patientsById[patientId];
    try {
      if (type === 'care_plan') {
        await materializeCarePlanResult(patient, monthStr, row.result_text);
      } else if (type === 'bhi_bn') {
        await materializeBhiBillingResult(patient, monthStr, row.result_text, row.assembler_ctx);
      } else if (type === 'bn_combined') {
        await materializeBillingResult(patient, monthStr, row.result_text, row.assembler_ctx);
      }
      applied++;
    } catch (e) {
      failed++;
      errors.push(String((e && e.message) || e));
    }
  }
  return { applied, failed, errors };
}

// Run the APCM/no-LLM billing tasks via the realtime generator (instant — no
// /ai call; APCM notes are assembled deterministically). Used for the
// localTasks split out by collectBatchRecords.
//
// Per-task try/catch: a bare sequential loop would let the first throwing task
// abort the rest, and the modal swallows the whole call — so an APCM patient's
// note could be silently not-generated with no failed count. Instead we isolate
// each task and RETURN { ran, failed, errors } so the caller can surface
// failures rather than discard them.
export async function runLocalTasks(localTasks, monthStr) {
  let ran = 0;
  let failed = 0;
  const errors = [];
  for (const lt of localTasks) {
    try {
      await generateAndSaveBillingNote(lt.task.patient, monthStr, {});
      ran++;
    } catch (e) {
      failed++;
      errors.push({ patientId: lt.task.patientId, error: String((e && e.message) || e) });
    }
  }
  return { ran, failed, errors };
}
